import type {
  OcrReview,
  Order,
  OrderPayload,
  ProductRule,
  ProductRulePayload,
  StatsResponse,
} from "../types";

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers);
  if (!(init?.body instanceof FormData)) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(path, {
    ...init,
    headers,
  });

  const data = (await response.json().catch(() => ({}))) as {
    error?: unknown;
  };
  if (!response.ok) {
    const message =
      typeof data?.error === "string" ? data.error : `请求失败：${response.status}`;
    throw new Error(message);
  }

  return data as T;
}

export const api = {
  stats() {
    return requestJson<StatsResponse>("/api/stats");
  },

  orders(q = "") {
    const query = q ? `?q=${encodeURIComponent(q)}` : "";
    return requestJson<{ orders: Order[] }>(`/api/orders${query}`);
  },

  createOrder(order: OrderPayload) {
    return requestJson<{ order: Order }>("/api/orders", {
      method: "POST",
      body: JSON.stringify(order),
    });
  },

  deleteOrder(id: string) {
    return requestJson<{ ok: true }>(`/api/orders/${id}`, {
      method: "DELETE",
    });
  },

  rules() {
    return requestJson<{ rules: ProductRule[] }>("/api/rules");
  },

  createRule(rule: ProductRulePayload) {
    return requestJson<{ rule: ProductRule }>("/api/rules", {
      method: "POST",
      body: JSON.stringify(rule),
    });
  },

  updateRule(id: string, rule: ProductRulePayload) {
    return requestJson<{ rule: ProductRule }>(`/api/rules/${id}`, {
      method: "PUT",
      body: JSON.stringify(rule),
    });
  },

  deleteRule(id: string) {
    return requestJson<{ ok: true }>(`/api/rules/${id}`, {
      method: "DELETE",
    });
  },

  uploadScreenshot(file: File) {
    const form = new FormData();
    form.append("screenshot", file);
    return requestJson<{ review: OcrReview; key: string }>("/api/upload", {
      method: "POST",
      body: form,
    });
  },

  recognize(reviewId: string) {
    return requestJson<{ review: OcrReview }>("/api/ocr", {
      method: "POST",
      body: JSON.stringify({ review_id: reviewId }),
    });
  },

  reviews() {
    return requestJson<{ reviews: OcrReview[] }>("/api/reviews");
  },

  updateReview(id: string, extractedJson: OrderPayload) {
    return requestJson<{ review: OcrReview }>(`/api/reviews/${id}`, {
      method: "PUT",
      body: JSON.stringify({ extracted_json: extractedJson }),
    });
  },

  confirmReview(id: string, order: OrderPayload) {
    return requestJson<{ order: Order }>(`/api/reviews/${id}/confirm`, {
      method: "POST",
      body: JSON.stringify({ order }),
    });
  },
};
